/**
 * The hours a property can be seen, as a week.
 *
 * On an owner's property the owner gives these in the app. On one the broker added there is no owner,
 * so nobody was giving them — and a listing with no windows cannot be booked at all: the slots endpoint
 * has nothing to offer and the viewing button leads nowhere. This is that missing half.
 *
 * A week of identical evenings is what almost everyone wants, so that is the default and the whole
 * thing fits on one screen: a row per day, a tick to open it, and two times.
 */
import { useState } from 'react';
import { Button, Field, Input } from '@/components/ui';
import { useI18n } from '@/i18n';

export interface Window {
  weekday: number;
  start: string;
  end: string;
}

/** Sunday first, the way the week runs in Saudi Arabia and the way `weekday` is numbered. */
const DAYS = [0, 1, 2, 3, 4, 5, 6];
export const DEFAULT_WINDOWS: Window[] = DAYS.map((weekday) => ({ weekday, start: '16:00', end: '20:00' }));

export function ViewingWindows({ value, onChange }: { value: Window[]; onChange: (v: Window[]) => void }) {
  const { t } = useI18n();
  const byDay = new Map(value.map((w) => [w.weekday, w]));
  const set = (weekday: number, patch: Partial<Window> | null) => {
    if (patch === null) return onChange(value.filter((w) => w.weekday !== weekday));
    const existing = byDay.get(weekday) ?? { weekday, start: '16:00', end: '20:00' };
    const next = { ...existing, ...patch };
    onChange([...value.filter((w) => w.weekday !== weekday), next].sort((a, b) => a.weekday - b.weekday));
  };
  const applyToAll = () => {
    const first = value[0];
    if (!first) return;
    onChange(DAYS.map((weekday) => ({ weekday, start: first.start, end: first.end })));
  };

  return (
    <div className="flex flex-col gap-2">
      {DAYS.map((d) => {
        const w = byDay.get(d);
        const on = !!w;
        const bad = !!w && w.end <= w.start;
        return (
          <div key={d} className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-2 min-w-[7.5rem] min-h-9 cursor-pointer">
              <input type="checkbox" checked={on} onChange={() => set(d, on ? null : {})} className="size-4 accent-[var(--accent)]" />
              <span className="text-[15px]">{t(`broker.windows.days.${d}`)}</span>
            </label>
            {on ? (
              <>
                <Input aria-label={`${t(`broker.windows.days.${d}`)} ${t('broker.windows.from')}`} type="time" value={w!.start} onChange={(e) => set(d, { start: e.target.value })} className="w-[7.5rem]" invalid={bad} />
                <span className="text-muted text-[13px]">{t('broker.windows.to')}</span>
                <Input aria-label={`${t(`broker.windows.days.${d}`)} ${t('broker.windows.to')}`} type="time" value={w!.end} onChange={(e) => set(d, { end: e.target.value })} className="w-[7.5rem]" invalid={bad} />
                {bad ? <span className="text-[13px] text-error">{t('broker.windows.endAfterStart')}</span> : null}
              </>
            ) : (
              <span className="text-[13px] text-muted">{t('broker.windows.closed')}</span>
            )}
          </div>
        );
      })}
      <div className="flex items-center gap-3 flex-wrap pt-1">
        <Button variant="secondary" className="min-h-9 text-[14px]" onClick={applyToAll} disabled={!value.length}>{t('broker.windows.applyToAll')}</Button>
        <Button variant="text" className="text-[14px]" onClick={() => onChange(DEFAULT_WINDOWS)}>{t('broker.windows.reset')}</Button>
        {!value.length ? <span className="text-[13px] text-warning">{t('broker.windows.noneWarning')}</span> : null}
      </div>
    </div>
  );
}

/** True when every open day reads forwards; an end at or before its start would book nothing. */
export function windowsValid(v: Window[]): boolean {
  return v.every((w) => w.end > w.start);
}

/** The editor as a standalone block with its own heading, for the dialog on an existing property. */
export function ViewingWindowsCard({ value, onChange }: { value: Window[]; onChange: (v: Window[]) => void }) {
  const { t } = useI18n();
  const [local, setLocal] = useState(value);
  return (
    <Field label={t('broker.windows.title')} hint={t('broker.windows.hint')}>
      <ViewingWindows
        value={local}
        onChange={(v) => {
          setLocal(v);
          onChange(v);
        }}
      />
    </Field>
  );
}
